<template>
  <div class="insidaPlanContent" @click="closeInsidePlanMenu">
    <div v-if="isLoading" class="spinner-overlay">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div class="container mr-4">
      <technical-local-summary
        :phase="techLocal.tl_status"
        :network-level="techLocal.tl_network_level"
        :cables-in="techLocal.cable_in"
        :cables-out="techLocal.cable_out"
        @set-tech-uuid="techLocalUuid = $event"
      />
      <inside-plan-diagram
        :racks="formattedRacks"
        :disabled="!(!!techLocal.tl_id)"
        @open-form="openForm"
        @open-menu="openMenu"
        @open-rack-menu="openRackMenu"
        @set-active-position="setActivePosition"
        @start-drag="startDrag"
        @end-drag="endDrag"
        @save-drawer-changes="saveDrawerChanges"
      />
    </div>
    <div class="container" style="overflow-y: visible">
      <selected-drawer
        :key="selectedDrawerRefreshToken"
        :selected-drawer="selectedDrawer"
        :connections="selectedDrawerConnections"
        :connections-were-changed="connectionsWereChanged"
        @open-tray-connection-menu="openTrayConnectionMenu"
        @open-tube-form="openAddTubeFiberForm"
        @open-cassette-form="openCassetteForm"
        @open-delete-cassette-dialog="openDeleteCassetteDialog"
        @save-changes="saveConnectionsChanges"
      />
    </div>
    <rack-drawer-form
      v-if="isFormOpen"
      :dialog-is-open="isFormOpen"
      :objectType="objectType"
      :item-to-edit="objectToEdit"
      :action="action"
      :rack-options="rackMaterialsOptions"
      :drawer-options="drawerMaterialsOptions"
      @add-rack="createRack"
      @edit-rack="editRack"
      @add-drawer="addDrawer"
      @edit-drawer="editDrawer"
      @close-rack-form="isFormOpen = !isFormOpen; itemToEdit = null;"
    />
    <drawer-options-menu
      v-else-if="isDrawerMenuOpen"
      :horizontal="menuHorizontalPostion"
      :vertical="menuVerticalPosition"
      :is-empty-position="isEmptyPosition"
      @open-form="openForm"
      @delete-drawer="deleteDrawer"
    />
    <rack-options-menu
      v-else-if="isRackMenuOpen"
      :horizontal="menuHorizontalPostion"
      :vertical="menuVerticalPosition"
      @open-form="openForm"
      @delete-rack="deleteRack"
    />
    <tray-connection-menu
      v-else-if="trayConnectionMenu.isOpen"
      :horizontal="menuHorizontalPostion"
      :vertical="menuVerticalPosition"
      @edit-fiber="openEditTubeFiberForm('fiber')"
      @edit-tube="openEditTubeFiberForm('tube')"
      @remove-fiber="openDeleteTubeFiberDialog('fiber')"
      @remove-tube="openDeleteTubeFiberDialog('tube')"
    />
    <add-tube-fiber-form
      v-else-if="addTubeFiberForm.isOpen"
      :dialog-is-open="addTubeFiberForm.isOpen"
      :available-fibers="availableFibers(techLocal.cable_in, techLocal.cable_out)"
      @add-object="addTubeFiber"
      @close-form="closeAddTubeFiberForm"
    />
    <edit-tube-fiber-form
      v-else-if="editTubeFiberForm.isOpen"
      :dialog-is-open="editTubeFiberForm.isOpen"
      :fiber-type-options="fiberTypeOptions"
      :object-data="editTubeFiberForm.fiberData"
      :object-type="editTubeFiberForm.objectTypeToEdit"
      @edit-object="editTubeFiber"
      @close-form="closeEditTubeFiberForm"
    />
    <delete-tube-fiber-dialog
      v-else-if="tubeFiberDialog.isOpen"
      :dialog-is-open="tubeFiberDialog.isOpen"
      @cancel-delete="closeTubeFiberDialog"
      @confirm-delete="deleteTubeFiber"
    />
    <delete-cassette-dialog
      v-else-if="deleteCassetteDialog.isOpen"
      :dialog-is-open="deleteCassetteDialog.isOpen"
      :cassette-label="deleteCassetteDialog.cassetteLabel"
      @cancel-delete="closeDeleteCassetteDialog"
      @confirm-delete="confirmDeleteCassetteInInsidePlan"
    />
    <add-or-edit-cassette-form
      v-if="cassetteForm.isOpen"
      :dialog-is-open="cassetteForm.isOpen"
      :item-to-edit="objectToEdit"
      :cassette-options="cassetteMaterialsOptions"
      @create-cassette="saveCassette"
      @edit-cassette="saveCassette"
      @close-cassette-form="closeCassetteForm"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import TechnicalLocalSummary from "./TechnicalLocalSummary.vue";
import InsidePlanDiagram from './InsidePlanDiagram.vue';
import RackDrawerForm from "./forms/RackDrawerForm.vue";
import AddOrEditCassetteForm from "./forms/AddOrEditCassetteForm.vue";
import AddTubeFiberForm from "./forms/AddTubeFiberForm.vue";
import EditTubeFiberForm from "./forms/EditTubeFiberForm.vue";
import DeleteTubeFiberDialog from "./forms/DeleteTubeFiberDialog.vue";
import DrawerOptionsMenu from "./menus/DrawerOptionsMenu.vue";
import RackOptionsMenu from "./menus/RackOptionsMenu.vue";
import TrayConnectionMenu from "./menus/TrayConnectionMenu.vue";
import SelectedDrawer from "./SelectedDrawer.vue";
import DeleteCassetteDialog from "./forms/DeleteCassetteDialog.vue";
import { v4 as uuidv4 } from 'uuid';
import { listsMixin } from "@/mixins/listsMixin";
import { insidePlanMixin } from "@/mixins/insidePlanMixin";
import _ from "lodash";

export default {
  name: "InsidePlan",

  components: {
    TechnicalLocalSummary,
    InsidePlanDiagram,
    RackDrawerForm,
    DrawerOptionsMenu,
    RackOptionsMenu,
    SelectedDrawer,
    TrayConnectionMenu,
    AddTubeFiberForm,
    DeleteTubeFiberDialog,
    EditTubeFiberForm,
    AddOrEditCassetteForm,
    DeleteCassetteDialog,
  },

  mixins: [ listsMixin, insidePlanMixin ],

  data() {
    return {
      activeObj: null,
      techLocalUuid: null,
      isFormOpen: false,
      objectType: null,
      isEmptyPosition: false,
      rackMaterialsOptions: [],
      drawerMaterialsOptions: [],
      cassetteMaterialsOptions: [],
      techLocal: {},
      racks: [],
      formattedRacks:[],
      selectedDrawer: null,
      fibersPortsCache: {
        fiber: {},
      },
      initialPhysicalStructure: null,
      deleteCassetteDialog: {
        isOpen: false,
        cassetteUuid: null,
        cassetteLabel: null,
      },
      selectedDrawerRefreshToken: 0,
    };
  },

  mounted() {
    this.getLists();
  },

  watch: {
    "techLocalUuid"(newValue) {
      this.techLocal = {};
      this.formattedRacks = [];
      this.selectedDrawer = null;
      this.getNetworkDiagram(newValue);
      this.allConnections = null;
    },

    "racks"(newValue){
      this.setRacksArray(newValue);
    },
  },

  computed: {
    selectedDrawerConnections() {
      return this.selectedDrawer && this.allConnections
        ? this.allConnections.filter(connection =>
            connection.trayParentDrawerId === this.selectedDrawer.dr_id &&
            (connection.destinationConnectionType !== 'port' ||
            connection.originConnectionType !== 'port')
          )
        : [];
    },

    cassetteType() {
      if (this.techLocalUuid) {
        return 'C';
      }
      return 'E';
      // TODO: remaining conditions
    },

    changedPhysicalStructure() {
      return _.differenceWith(this.formattedRacks, this.initialPhysicalStructure, _.isEqual);
    },
  },

  methods: {
    ...mapActions([
      "fetchPhysicalConnections",
      "fetchStockMaterialsList",
      "addRack",
      "updateRack",
      "removeRack",
      "createDrawer",
      "updateDrawer",
      "removeDrawer",
      "fetchObjectSplicingConnections",
      "saveObjectSplicingConnections",
      "createCassette",
      "editCassette",
      "deleteCassette",
    ]),

    async getNetworkDiagram(technicalLocalUUID) {
      if(technicalLocalUUID){
        const workspaceUuid = sessionStorage.getItem("workspace_uuid");
        const obj =
          {
            workspace_uuid: workspaceUuid,
            object: {
              uuid: technicalLocalUUID,
              object_type: "technical_local"
            },
            include: [
              "racks.drawers",
              "racks.drawers.cassettes",
              "cableIn",
              "cableOut",
              "cableIn.fibers",
              "cableOut.fibers",
            ]
          };
        try {
          this.isLoading = true;
          const response = await this.fetchPhysicalConnections(obj);
          const {racks, ...rest} = response;
          this.techLocal = {...rest};
          this.racks = [...racks];
          this.getAllTechLocalConnections(technicalLocalUUID);
          this.$nextTick(() => {
            this.setInitialPhysicalStructure(this.racks);
          })

          if (this.selectedDrawer && racks.length > 0) {
            let updatedRack = racks.find(rack => rack.ra_id === this.selectedDrawer.dr_rack_id);
            let updatedDrawer = updatedRack.drawers.find(drawer => drawer.dr_id === this.selectedDrawer.dr_id);
            this.selectedDrawer = {...updatedDrawer};
          }
        } catch(err){
          this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
        } finally {
          this.isLoading = false;
        }
      }
    },

    /**
     * Saves the changes made to the connections
     */
    async saveConnectionsChanges() {
      try {
        this.isLoading = true;
        let dataToSend = this.createConnectionsDataToSend(this.changedConnections);
        await this.saveObjectSplicingConnections({
          objectEndpoint: "technicallocals",
          objectUUID: this.techLocalUuid,
          updatedConnections: { connections: dataToSend, physical: true },
        })

        this.getNetworkDiagram(this.techLocalUuid);
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    async createRack(obj){
      try{
        this.isLoading = true;
        const response = await this.addRack([{...obj, technical_local_id: this.techLocal.tl_id}]);
        this.racks = [{...response[0], drawers: []}, ...this.racks];
        this.getNetworkDiagram(this.techLocalUuid);
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
      }finally{
        this.isLoading = false;
        this.closeForm();
      }
    },

    async editRack(obj){
      const {label, status} = obj;
      try{
        this.isLoading = true;
        const response = await this.updateRack([{label, status, uuid: this.objectToEdit.ra_uuid}]);
        const racksCopy = [...this.racks];
        racksCopy.forEach(rack => {
          if(rack.ra_id === response[0].ra_id){
            rack.ra_label = response[0].ra_label;
            rack.ra_status = response[0].ra_status;
          }
        });
        this.racks = [...racksCopy];
      }catch(err){
        this.handleError(err);
      }finally{
        this.isLoading = false;
        this.closeForm();
      }
    },

    async deleteRack(){
      try{
        this.isLoading = true;
        await this.removeRack(this.objectToEdit.ra_uuid);
        this.getNetworkDiagram(this.techLocalUuid);
        this.$nextTick(() => {
          if (this.selectedDrawerBelongsToRackToDelete) {
            this.selectedDrawer = null;
          }
          const racksCopy = [];
          this.racks.forEach(rack => {
            if(rack.ra_uuid !== this.objectToEdit.ra_uuid){
              racksCopy.push({...rack});
            }
          });
          this.racks = [...racksCopy];
        })
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Checks if the selected drawer belongs to the rack to be deleted
     */
    selectedDrawerBelongsToRackToDelete() {
      let rackToDelete = this.racks.find(rack => rack.ra_uuid === this.objectToEdit.ra_uuid);
      return rackToDelete && this.selectedDrawer ? rackToDelete.ra_id === this.selectedDrawer.dr_rack_id : false;
    },

    async addDrawer(obj){
      try{
        this.isLoading = true;
        await this.saveDrawerChanges()
        const response = await this.createDrawer([{...obj, rack_id: this.activeObj.rackId, u_position: this.activeObj.index}]);
        this.activeObj.data = {...response[0]}
        const racksCopy = [...this.racks];
        racksCopy.forEach(rack =>{
          if(rack.ra_id === response[0].dr_rack_id){
            rack.drawers.push({...response[0]});
          };
        });
        this.racks = [...racksCopy];
        this.getNetworkDiagram(this.techLocalUuid);
      }catch(err){
        console.error(err);
        this.handleError(err);
      }finally{
        this.isLoading = false;
        this.closeForm();
      }
    },

    handleError(err) {
      console.error(err);
      if (err.data) {
        this.$toasted.global.toast_error(err.data.message);
      } else if (err.response) {
        this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
        if (err.response.data.data === 'The slots in the rack are already in use! Please choose another position!') {
          this.$toasted.global.toast_error('Please try saving your changes before creating the drawer.')
        }
      } else if (err) {
        this.$toasted.global.toast_error(err.data);
      }
    },

    async editDrawer(obj){
      const {label, status} = obj;
      try{
        this.isLoading = true;
        const response = await this.updateDrawer([{label, status, uuid: this.objectToEdit.data.dr_uuid, rack_id: this.objectToEdit.data.dr_rack_id, u_position: this.objectToEdit.data.dr_u_position}]);
        const racksCopy = [...this.racks];
        racksCopy.forEach(rack =>{
          if(rack.ra_id === response[0].dr_rack_id){
            const drawersCopy = [...rack.drawers];
            for(let i = 0; i < drawersCopy.length; i++){
              if(drawersCopy[i].dr_id === response[0].dr_id){
                drawersCopy[i].dr_label = response[0].dr_label;
                drawersCopy[i].dr_status = response[0].dr_status;
              }
            }
          };
        });
        this.racks = [...racksCopy];
      }catch(err){
        this.handleError(err);
      }finally{
        this.isLoading = false;
        this.closeForm();
      }
    },

    async deleteDrawer(){
      try{
        this.isLoading = true;
        await this.saveDrawerChanges()
        if (this.objectToEdit.data.dr_uuid === this.selectedDrawer.dr_uuid) {
          this.selectedDrawer = null;
        }
        await this.removeDrawer(this.objectToEdit.data.dr_uuid);
        this.getNetworkDiagram(this.techLocalUuid);
        this.$nextTick(() => {
          const racksCopy = [...this.racks];
          racksCopy.forEach(rack =>{
            if(rack.ra_id === this.objectToEdit.data.dr_rack_id){
              const newDrawers = [];
              rack.drawers.forEach(drawer =>{
                if(drawer.dr_id !== this.objectToEdit.data.dr_id){
                  newDrawers.push({...drawer});
                }
              });
              rack.drawers = [...newDrawers];
            };
          });
          this.racks = [...racksCopy];
        })
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
      } finally {
        this.isLoading = false;
      }
    },

    openForm(type, action){
      if(this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      if(this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      if(this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      this.objectType = type;
      this.action = action;
      this.isFormOpen = !this.isFormOpen;
    },

    closeForm() {
      this.isFormOpen = !this.isFormOpen;
      this.objectToEdit = null;
      this.action = null;
      this.objectType = null;
    },

    openMenu(horizontal, vertical, isEmptyPosition, position){
      if(!position.active){
        this.setActivePosition(position);
      }
      if(this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      if(this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      if(!this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      this.objectToEdit = {...position};
      this.menuHorizontalPostion = horizontal - 30;
      this.menuVerticalPosition = vertical - 45;
      this.isEmptyPosition = isEmptyPosition;
    },

    openRackMenu(horizontal, vertical, rack){
      this.setActiveRack(rack.ra_id);
      if(this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      if(this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      if(!this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      this.objectToEdit = {...rack}
      this.menuHorizontalPostion = horizontal - 30;
      this.menuVerticalPosition = vertical - 45;
    },

    async getLists(){
      try {
        this.isLoading = true;
        this.rackMaterialsOptions = await this.getMaterialOptionsByFamilyID("rc");
        this.drawerMaterialsOptions = await this.getMaterialOptionsByFamilyID("dw");
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    setRacksArray(newValue){
      const modifiedArray = [];
      newValue.forEach(rack => {
        const drawersArray = [...this.createDrawersArray(rack)];
        const {drawers, ...rest} = rack;
        modifiedArray.push({...rest, drawers: drawersArray, active: false})
      });
      this.formattedRacks = [...modifiedArray];
    },

    //create drawer array of rack
    createDrawersArray(rack){
      const drawersArray = Array(Number(rack.ra_u_count));
      //populate array with objects
      for(let i = 0; i < drawersArray.length; i++){
        drawersArray[i] = {
          positionUuid: uuidv4(),
          index: i,
          active: false,
          opacity: false,
          rackId: rack.ra_id
        };
      }
      rack.drawers.forEach((drawer) => {
        const start = Number(drawer.dr_u_position);
        const end = start - (Number(drawer.dr_u_size) - 1);
        for(let i = start; i >= end; i--){
          if(this.activeObj && this.activeObj.data && (this.activeObj.data.dr_id === drawer.dr_id)){
            const topOrBottom = this.isTopOrBottom(start, end, i, Number(drawer.dr_u_size));
            drawersArray[i].active = true;
            drawersArray[i] = {...drawersArray[i], parentDrawerId: drawer.dr_id, data: {...drawer, ...topOrBottom}};
          }else if(this.activeObj && (this.activeObj.index === drawer.dr_u_position)){
            const topOrBottom = this.isTopOrBottom(start, end, i, Number(drawer.dr_u_size));
            drawersArray[i].active = true;
            drawersArray[i] = {...drawersArray[i], parentDrawerId: drawer.dr_id, data: {...drawer, ...topOrBottom}};
          }else{
            const topOrBottom = this.isTopOrBottom(start, end, i, Number(drawer.dr_u_size));
            drawersArray[i] = {...drawersArray[i], parentDrawerId: drawer.dr_id, data: {...drawer, ...topOrBottom}};
          }
        }
      });
      return drawersArray;
    },

    isTopOrBottom(start, end, index, size){
      let obj;
      if(size === 1){
        obj = { topOrBottom: 'position-border-top-bottom'}
      }else{
        if(start === index){
          obj = { topOrBottom: 'position-border-top'}
        }
        if(end === index){
          obj = { topOrBottom: 'position-border-bottom'}
        }
      }
      return obj;
    },

    setActivePosition(obj){
      //remove opacity if it is active
      this.removeOpacity(obj);
      //get id of parent drawer of positions
      const drawerParentId = obj.parentDrawerId;
      this.activeObj = obj;
      const rack = this.formattedRacks.find(item => item.ra_id === obj.rackId);
      if(rack.active){
        rack.active = false;
      }
      const drawersArray = [];
      //need to check rackID
      rack.drawers.forEach(drawer =>{
        const copy = {...drawer};
        if(drawerParentId){
          if(drawer.parentDrawerId !== drawerParentId && drawer.active){
            copy.active = false;
          }
          if(drawer.parentDrawerId === drawerParentId){
            copy.active = !copy.active;
            if (copy.active) {
              this.selectedDrawer = obj.data;
            } else {
              this.selectedDrawer = null;
            }
          }
        }else{
          if(drawer.positionUuid !== obj.positionUuid && drawer.active){
            copy.active = false;
          }
          if(drawer.positionUuid === obj.positionUuid){
            copy.active = !copy.active;
            if (copy.active) {
              this.selectedDrawer = obj.data;
            } else {
              this.selectedDrawer = null;
            }
          }
        }
        drawersArray.push(copy);
      });
      rack.drawers = drawersArray;
      for(let i=0; i < this.formattedRacks.length; i++){
        if(this.formattedRacks[i].ra_id !== obj.rackId){
          const drawersArray = [...this.formattedRacks[i].drawers];
          drawersArray.forEach(drawer => {
            if(drawer.active){
              drawer.active = false;
            }
          });
          this.formattedRacks[i].drawers = [...drawersArray];
        }
      }
    },

    setActiveRack(id){
      this.formattedRacks.forEach(rack =>{
        if(rack.ra_id !== id && rack.active){
          rack.active = false;
        }
        if(rack.ra_id === id){
          rack.active = true;
        }
        rack.drawers.forEach(drawer =>{
          if(drawer.active){
            drawer.active = false;
          }
        })
      })
    },

    startDrag(evt, position) {
      evt.dataTransfer.dropEffect = 'move'
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.setData('positionUuid', position.positionUuid);
      this.setOpacity(position.positionUuid);

    },

    endDrag(evt, dragEndPosition) {
      const dragStartUuid = evt.dataTransfer.getData('positionUuid');
      const dragStartPosition = this.findPosition(dragStartUuid);
      const startRackId = dragStartPosition.rackId;
      const endRackId = dragEndPosition.rackId;
      if(dragStartPosition.data){
        const startPositions = this.findAllPositions(dragStartPosition.data);
        if(dragEndPosition.data){
          //check if it has enough space
          const startPositions = this.findAllPositions(dragStartPosition.data);
          const endPositions = this.findAllPositions(dragEndPosition.data);
          this.changeDrawers(dragStartPosition, dragEndPosition, startPositions, endPositions);
        }else{
          // set position on drop side (rack to add drawers) and clear on drag side
          this.changeToFreePositions(dragEndPosition , startPositions.length, startPositions, dragStartPosition.data, startRackId, endRackId);
        }
      }
      this.removeOpacity(dragStartPosition);
    },

    findPosition(uuid){
      let obj;
      let isfound = false;
      this.formattedRacks.forEach(rack => {
        if(!isfound){
          rack.drawers.forEach(drawer => {
            if(drawer.positionUuid === uuid){
              isfound = true;
              obj = {...drawer};
              return
            }
          })
        }
      })
      return obj;
    },

    findAllPositions(positionData){
      const allPositions = [];
      this.formattedRacks.forEach(rack => {
        rack.drawers.forEach(drawer => {
          if(drawer.parentDrawerId && drawer.parentDrawerId === positionData.dr_id){
            allPositions.push({...drawer})
          }
        })
      })
      return allPositions;
    },

    changeToFreePositions(endPosition, numberOfPositions, startPositions, startPosition, startRackId, endRackId){
      const start = endPosition.index;
      const end = start - (numberOfPositions -1);
      const startPositionCopy = {...startPosition};
      const endPositionCopy = {...endPosition};
      // if end is greater or equal to 0, then it is posible to change position
      if(end >= 0){
        let allPositionsAreEmpty = true;
        let drawersArray;
        //copy array of drawers of rack
        this.formattedRacks.forEach(rack => {
          if(rack.ra_id === endPosition.rackId){
            drawersArray = [...rack.drawers]
          }
        });
        //check if all positions are free
        for(let i = start ; i >= end ; i--){
          if(drawersArray[i].data && drawersArray[i].data.dr_id !== startPosition.dr_id){
            allPositionsAreEmpty = false;
          }
        }
        //if position are free, then change positions
        if(allPositionsAreEmpty){
          //change positions
          let j = startPositions.length - 1;
          for(let i = start ; i >= end ; i--){
            startPositions[j].data.dr_rack_id = endPosition.rackId;
            startPositions[j].data.dr_u_position = endPosition.index;
            drawersArray[i] = {...drawersArray[i], parentDrawerId: startPositions[j].parentDrawerId, data: startPositions[j].data, active: startPositions[j].active}
            j = j - 1;
          }
          //change position on end side
          this.formattedRacks.forEach(rack => {
            if(rack.ra_id === endPosition.rackId){
              rack.drawers = [...drawersArray]
            }
          });
          //clear drag positions
          this.clearToFreePositions(startPositionCopy, startPositions, endPositionCopy,  startRackId, endRackId);
        }else{
          this.$toasted.global.toast_error('Not enough space');
        }
      }
      else{
        this.$toasted.global.toast_error('Not enough space');
      }
    },

    clearToFreePositions(startPosition, startPositions, endPosition,  startRackId, endRackId){
      if(startRackId !== endRackId){
        this.formattedRacks.forEach(rack => {
          if(rack.ra_id === startPositions[0].rackId){
            const drawersArray = [...rack.drawers];
            startPositions.forEach(position =>{
              position.active = false;
              const {data, parentDrawerId, ...rest} = position;
              drawersArray[position.index] = rest;
            })
            rack.drawers = [...drawersArray];
          }
        });
      }else{
        const difference = endPosition.index - startPosition.dr_u_position;
        let start = endPosition.index - (startPosition.dr_u_size);
        let end = start - difference;
        //goes up
        if(difference > 0){
          this.formattedRacks.forEach(rack => {
            if(rack.ra_id === startPositions[0].rackId){
              for(let i = start; i >= end; i--){
                if(rack.drawers[i] && rack.drawers[i].parentDrawerId && (rack.drawers[i].parentDrawerId === startPosition.dr_id)){
                  rack.drawers[i].active = false;
                  const  {data, parentDrawerId, ...rest} = rack.drawers[i];
                  rack.drawers[i] = rest;
                }
              }
            }
          })
        }else{
          //goes down
          start = Number(startPosition.dr_u_position);
          end = start - (Number(startPosition.dr_u_size) - 1);
          this.formattedRacks.forEach(rack => {
            if(rack.ra_id === startPositions[0].rackId){
              for(let i = start; i >= end; i--){
                rack.drawers[i].active = false;
                const  {data, parentDrawerId, ...rest} = rack.drawers[i];
                rack.drawers[i] = rest;
              }
            }
          })
        }
      }
    },

    changeDrawers(dragStartPosition, dragEndPosition, startPositions, endPositions){
      if(dragStartPosition.parentDrawerId !== dragEndPosition.parentDrawerId){
        let dragStart = dragStartPosition.data.dr_u_position;
        let dragEnd = dragStart - (dragEndPosition.data.dr_u_size - 1);
        let dropStart = dragEndPosition.data.dr_u_position;
        let dropEnd = dropStart - (dragStartPosition.data.dr_u_size - 1);
        if(dragEnd >= 0 && dropEnd >= 0){
          const isChangeAllowed = this.changeDrawerIsPossible(dragStart, dragEnd, dropStart, dropEnd, dragStartPosition.rackId === dragEndPosition.rackId, dragStartPosition, dragEndPosition);
          if(isChangeAllowed){
            //clear drawers in racks
            this.clearDrawersPositions(dragStartPosition, startPositions, dragEndPosition, endPositions);
            let dragDrawers;
            let dropDrawers;
            this.formattedRacks.forEach(rack => {
              if(rack.ra_id === dragStartPosition.rackId){
                dragDrawers = [...rack.drawers]
              }else if(rack.ra_id === dragEndPosition.rackId){
                dropDrawers = [...rack.drawers]
              }
            });
            if(dragStartPosition.rackId !== dragEndPosition.rackId){
              let k = endPositions.length - 1;
              for(let i = dragStart; i >= dragEnd; i--){
                endPositions[k].data.dr_rack_id = dragStartPosition.rackId;
                endPositions[k].data.dr_u_position = dragStart;
                dragDrawers[i] = {...dragDrawers[i], parentDrawerId: endPositions[k].parentDrawerId, data: endPositions[k].data, active: endPositions[k].active}
                k = k - 1;
              }
              let j = startPositions.length - 1;
              for(let i = dropStart; i >= dropEnd; i--){
                startPositions[j].data.dr_rack_id = dragEndPosition.rackId;
                startPositions[j].data.dr_u_position = dropStart;
                dropDrawers[i] = {...dropDrawers[i], parentDrawerId: startPositions[j].parentDrawerId, data: startPositions[j].data, active: startPositions[j].active};
                j = j - 1;
              }
            }else{
              let k = endPositions.length - 1;
              for(let i = dragStart; i >= dragEnd; i--){
                endPositions[k].data.dr_rack_id = dragStartPosition.rackId;
                endPositions[k].data.dr_u_position = dragStart;
                dragDrawers[i] = {...dragDrawers[i], parentDrawerId: endPositions[k].parentDrawerId, data: endPositions[k].data, active: endPositions[k].active}
                k = k - 1;
              }
              let j = startPositions.length - 1;
              for(let i = dropStart; i >= dropEnd; i--){
                startPositions[j].data.dr_rack_id = dragEndPosition.rackId;
                startPositions[j].data.dr_u_position = dropStart;
                dragDrawers[i] = {...dragDrawers[i], parentDrawerId: startPositions[j].parentDrawerId, data: startPositions[j].data, active: startPositions[j].active}
                j = j - 1;
              }
            }
            this.formattedRacks.forEach(rack => {
              if(rack.ra_id === dragStartPosition.rackId){
                rack.drawers = [...dragDrawers];
              }else if(rack.ra_id === dragEndPosition.rackId){
                rack.drawers = [...dropDrawers];
              }
            });
          }else{
            this.$toasted.global.toast_error('This is not allowed');
          }
        }else{
          this.$toasted.global.toast_error('This is not allowed');
        }
      }
    },

    clearDrawersPositions(dragStartPosition, startPositions, dragEndPosition, endPositions){
      if(dragStartPosition.rackId !== dragEndPosition.rackId){
        this.formattedRacks.forEach(rack =>{
          const drawersCopy = [...rack.drawers];
          if(rack.ra_id === dragStartPosition.rackId){
            startPositions.forEach(position =>{
              for(let i = 0; i < drawersCopy.length; i++){
                if(drawersCopy[i].positionUuid === position.positionUuid){
                  const  {data, parentDrawerId, ...rest} = drawersCopy[i];
                  drawersCopy[i] = {...rest};
                }
              }
            });
          }else if(rack.ra_id === dragEndPosition.rackId){
            endPositions.forEach(position =>{
              for(let i = 0; i < drawersCopy.length; i++){
                if(drawersCopy[i].positionUuid === position.positionUuid){
                  const  {data, parentDrawerId, ...rest} = drawersCopy[i];
                  drawersCopy[i] = {...rest};
                }
              }
            });
          }
          rack.drawers = [...drawersCopy];
        });
      }else{
        this.formattedRacks.forEach(rack =>{
          const drawersCopy = [...rack.drawers];
          if(rack.ra_id === dragStartPosition.rackId){
            startPositions.forEach(position =>{
              for(let i = 0; i < drawersCopy.length; i++){
                if(drawersCopy[i].positionUuid === position.positionUuid){
                  const  {data, parentDrawerId, ...rest} = drawersCopy[i];
                  drawersCopy[i] = {...rest};
                }
              }
            });
            endPositions.forEach(position =>{
              for(let i = 0; i < drawersCopy.length; i++){
                if(drawersCopy[i].positionUuid === position.positionUuid){
                  const  {data, parentDrawerId, ...rest} = drawersCopy[i];
                  drawersCopy[i] = {...rest};
                }
              }
            });
          }
          rack.drawers = [...drawersCopy];
        });
      }
    },

    changeDrawerIsPossible(dragStart, dragEnd, dropStart, dropEnd, isSameRack,dragStartPosition, dragEndPosition){
      let isPosible = true;
      let dragDrawers;
      let dropDrawers;
      this.formattedRacks.forEach(rack => {
        if(rack.ra_id === dragStartPosition.rackId){
          dragDrawers = [...rack.drawers]
        }else if(rack.ra_id === dragEndPosition.rackId){
          dropDrawers = [...rack.drawers]
        }
      });
      if(!isSameRack){
        for(let i = dragStart; i >= dragEnd; i--){
          if(dragDrawers[i].data && (dragDrawers[i].parentDrawerId !== dragStartPosition.parentDrawerId)){
            isPosible = false;
          }
        }
        for(let i = dropStart; i >= dropEnd; i--){
          if(dropDrawers[i].data && (dropDrawers[i].parentDrawerId !== dragEndPosition.parentDrawerId)){
            isPosible = false;
          }
        }
      }else{
        for(let i = dragStart; i >= dragEnd; i--){
          if(dragDrawers[i].data && (dragDrawers[i].parentDrawerId !== dragStartPosition.parentDrawerId)){
            isPosible = false;
          }
        }
        for(let i = dropStart; i >= dropEnd; i--){
          if(dragDrawers[i].data && (dragDrawers[i].parentDrawerId !== dragEndPosition.parentDrawerId)){
            isPosible = false;
          }
        }
      }
      return isPosible;
    },

    async saveDrawerChanges(){
      try{
        this.isLoading = true
        const drawers = this.getDrawersToSend();
        await this.updateDrawer(drawers);
        const workspaceUuid = sessionStorage.getItem("workspace_uuid");
        const obj =
          {
            workspace_uuid: workspaceUuid,
            object: {
              uuid: this.techLocalUuid,
              object_type: "technical_local"
            },
            include: [
              "racks.drawers",
              "racks.drawers.cassettes",
              "cableIn",
              "cableOut",
              "cableIn.fibers",
              "cableOut.fibers",
              "splitters.inPorts",
              "splitters.outPorts"
            ]
          };
        const response = await this.fetchPhysicalConnections(obj);
        const {racks, ...rest} = response;
        this.racks = [...racks];
        this.$nextTick(() => {
          this.setInitialPhysicalStructure(this.racks);
        });
      }catch(err){
        console.error(err);
        this.racks = [...this.racks];
        this.$toasted.global.toast_error(err.response.data.data ? err.response.data.data : err.response.data.message);
      }finally{
        this.isLoading = false
      }
    },

    setInitialPhysicalStructure(racks) {
      this.setRacksArray(racks);
      this.$nextTick(() => {
        this.initialPhysicalStructure = _.cloneDeep(this.formattedRacks);
      })
    },

    getDrawersToSend(){
      const drawersToSend = [];
      this.formattedRacks.forEach(rack => {
        rack.drawers.forEach(drawer =>{
          if(drawer.data){
            const {data} = drawer;
            const {dr_label, dr_uuid, dr_rack_id, dr_u_position} = data;
            drawersToSend.push({
              uuid: dr_uuid,
              label: dr_label,
              rack_id: dr_rack_id,
              u_position: dr_u_position,
            });
          };
        });
      });
      return drawersToSend;
    },

    setOpacity(positionUuid){
      const dragStartPosition = this.findPosition(positionUuid);
      const start = dragStartPosition.data.dr_u_position;
      const end = start - (dragStartPosition.data.dr_u_size - 1);
      this.formattedRacks.forEach(rack =>{
        if(rack.ra_id === dragStartPosition.rackId){
          for(let i = start; i >= end; i--){
            rack.drawers[i].opacity = true;
          }
        }
      });
    },

    removeOpacity(startPosition){
      this.formattedRacks.forEach(rack =>{
        if(rack.ra_id === startPosition.rackId){
          rack.drawers.forEach(drawer =>{
            if(drawer.opacity){
              drawer.opacity = false;
            }
          })
        }
      });
    },

    async getAllTechLocalConnections(techLocalUuid) {
      try {
        this.isLoading = true;
        const connections = await this.getAllConnections("technicallocals", techLocalUuid);

        // populate object with all fiber and ports
        this.techLocal.cable_in.forEach((cable)=>{
          cable.fibers.forEach((fiber)=>{
            this.fibersPortsCache['fiber'][fiber.fb_id] = {
              fiber,
              cable
            };
          })
        });
        this.techLocal.cable_out.forEach((cable)=>{
          cable.fibers.forEach((fiber)=>{
            this.fibersPortsCache['fiber'][fiber.fb_id] = {
              fiber,
              cable
            };
          })
        });

        this.$nextTick(() => {
          // in the insidePlanMixin
          this.createConnectionsArray(connections);
        });

      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getAllConnections(objectEndpoint, objectUuid) {
      try {
        this.isLoading = true;
        const response = await this.fetchObjectSplicingConnections({
          objectEndpoint: objectEndpoint,
          objectUUID: objectUuid
        });
        return response;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    async saveCassette(data) {
      try {
        this.isLoading = true;
        let dataToSend = this.getCassetteDataToSend(data);
        if (this.action === "create") {
          await this.createCassette(dataToSend);
        } else if (this.action === "edit") {
          await this.editCassette(dataToSend);
        }

        this.getNetworkDiagram(this.techLocalUuid);
      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error.data);
      } finally {
        this.isLoading = false;
      }
      this.closeCassetteForm();
    },

    getCassetteDataToSend(cassette) {
      let cassetteMaterialInfo = this.cassetteMaterialsOptions.find(ct => ct.sm_id === cassette.material_id);
      let dataToSend = {};
      if (this.action === 'create') {
        dataToSend = {
          label: cassette.label,
          material_id: cassette.material_id,
          capacity: cassetteMaterialInfo.family_specific_fields.smct_capacity,
          drawer_id: this.selectedDrawer.dr_id,
          type: this.cassetteType,
        }
      } else if (this.action === "edit") {
        dataToSend = {
          label: cassette.label,
          material_id: this.objectToEdit.ct_material_id,
          capacity: this.objectToEdit.ct_capacity,
          drawer_id: this.objectToEdit.ct_drawer_id,
          type: this.objectToEdit.ct_type,
          id: this.objectToEdit.ct_id,
          uuid: this.objectToEdit.ct_uuid,
        }
      }
      return [dataToSend];
    },

    confirmDeleteCassetteInInsidePlan() {
      try {
        this.confirmDeleteCassette();
        this.$nextTick(() => {
          this.getNetworkDiagram(this.techLocalUuid);
        })
      } catch (e) {
        console.error(e);
      }
    },
  }
}
</script>

<style scoped>
.insidaPlanContent {
  position: relative;
  width: 100%;
  height: 100%;
  background: #9c9b9b;
  display: flex;
  padding: 20px 20px 20px 30px;
}
.container{
  flex: 1;
  overflow-y: scroll;
}
.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}
</style>


// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/InsidePlan/InsidePlan.vue